import React, { FC, lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { AppHelmetProvider } from '@dxb-web-ui-framework/internationalization/contexts/AppHelmet/AppHelmetProvider';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { Box, makeStyles } from '@dxb-web-ui-framework/design-system';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useAppAuthenticationState } from '@dxb-web-ui-framework/iam/contexts/AppAuthentication/hooks';
import { SecureRoute, EligibleRoute } from '../../packages/iam/src/components';
import { useLanguage } from '../../packages/internationalization/src/contexts/Internationalization/hooks';
import { Header } from '../../packages/content-composer/src/components/Partials/Header';
import '../../packages/internationalization/src/utils/i18n';
import { ECOMMBXLoader } from '../../packages/content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { DashboardProvider } from '../../packages/dashboard/src/context/DashboardProvider';
import { AccountListProvider } from '../../packages/account/src/contexts/AccountList/AccountListProvider';
import { RecurringOrdersProvider } from '../../packages/fundTransfer/src/contexts/RecurringOrders/RecurringOrdersProvider';
import { GoogleAnalyticsPageView } from '../../packages/telemetry/src/components/GoogleAnalyticsPageView';
import { FundTransferProvider } from '../../packages/fundTransfer/src/contexts/FundTransfer/FundTransferProvider';
import { DeviceManagementProvider } from '../../packages/deviceManagement/src/contexts/DeviceManagement/DeviceManagementProvider';
import { ExchangeCalculateProvider } from '../../packages/fundTransfer/src/contexts/ExchangeCalculate/ExchangeCalculateProvider';
import { FavouriteTransfersProvider } from '../../packages/fundTransfer/src/contexts/FavouriteTransfers/FavouriteTransfersProvider';
import { ActivityLogProvider } from '../../packages/activityLog/src/contexts/ActivityLog/ActivityLogProvider';
import { BeneficiariesProvider } from '../../packages/beneficiary/src/contexts/Beneficiary/BeneficiariesProvider';
import { PayrollProvider } from '../../packages/payroll/src/contexts/PayrollFile/PayrollProvider';
import { CommunicationProvider } from '../../packages/userProfile/src/contexts/Communication';
import { EnhancedAliasProvider } from '../../packages/account/src/contexts/Alias/AliasProvider';
import { useIsCardHolder } from '../../packages/transaction/src/contexts/Parameters/hooks';
import { CardsProvider } from '../../packages/card/src/contexts/Card';
import { CardDetailsWrapper } from '../../packages/card/src/contexts/CardDetails';
import { CardActionsProvider } from '../../packages/card/src/contexts/CardActions';
import { AccountsRouter, ConsolidatedTransactionsRouter } from './Router';
import { CorporateCardsRouter, IndividualCardsRouter } from './Router/CardsRouter';
import { WrappedAuthorizationsDetailsPage } from './pageWrappers';

const DashboardPage = lazy(
  () => import(/* webpackChunkName: "DashboardPageComponent" */ '../../packages/dashboard/src/pages/DashboardPage'),
);

const DashboardCardHolderPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DashboardPageComponent" */ '../../packages/dashboard/src/pages/DashboardCardHolderPage'
    ),
);

const UserProfilePage = lazy(
  () =>
    import(/* webpackChunkName: "UserProfilePageComponent" */ '../../packages/userProfile/src/pages/UserProfilePage'),
);

const FavoriteBeneficiariesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "FavoriteBeneficiariesPage" */ '../../packages/beneficiary/src/pages/FavoriteBeneficiaries'
    ),
);

const CreateFavoriteBeneficiaryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "FavoriteBeneficiariesPage" */ '../../packages/beneficiary/src/pages/CreateFavoriteBeneficiary'
    ),
);

const CommunicationPreferencesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CommunicationPreferencesPageComponent" */ '../../packages/userProfile/src/pages/CommunicationPreferencesPage'
    ),
);

const GDPRPage = lazy(
  () => import(/* webpackChunkName: "GDPRPageComponent" */ '../../packages/userProfile/src/pages/GDPRPage'),
);

const DeviceManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DeviceManagementPageComponent" */ '../../packages/deviceManagement/src/pages/DeviceManagementPage'
    ),
);

const ErrorPage404 = lazy(
  () => import(/* webpackChunkName: "ErrorPage404Component" */ '../../packages/static-content/src/pages/ErrorPage404'),
);

const SecurityPreferencesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "SecurityPreferencesPageComponent" */ '../../packages/security-settings/src/pages/SecurityPreferencesPage'
    ),
);

const SecurityPreferencesChangePasswordPage = lazy(
  () =>
    import(
      /* webpackChunkName: "SecurityPreferencesChangePasswordPageComponent" */ '../../packages/security-settings/src/pages/SecurityPreferencesChangePasswordPage'
    ),
);

const SecurityPreferencesChangeUsernamePage = lazy(
  () =>
    import(
      /* webpackChunkName: "SecurityPreferencesChangePasswordPageComponent" */ '../../packages/security-settings/src/pages/SecurityPreferencesChangeUsernamePage'
    ),
);

const ErrorPage = lazy(
  () => import(/* webpackChunkName: "ErrorPageComponent" */ '../../packages/content-composer/src/pages/ErrorPage'),
);

const SuccessPage = lazy(
  () => import(/* webpackChunkName: "SuccessPageComponent" */ '../../packages/content-composer/src/pages/SuccessPage'),
);

const AuthorizationsListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "AuthorizationsListComponent" */ '../../packages/authorizations/src/pages/AuthorizationsListPage'
    ),
);

const MassPaymentsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "MassPaymentsPageComponent" */ '../../packages/fundTransfer/src/pages/MassPaymentsPage'
    ),
);

const PayrollPage = lazy(
  () => import(/* webpackChunkName: "PayrollPageComponent" */ '../../packages/payroll/src/pages/PayrollPage'),
);

const NewPayrollPage = lazy(
  () => import(/* webpackChunkName: "NewPayrollPageComponent" */ '../../packages/payroll/src/pages/NewPayrollPage'),
);

const PayrollDetailsPage = lazy(
  () =>
    import(/* webpackChunkName: "PayrollDetailsPageComponent" */ '../../packages/payroll/src/pages/PayrollDetailsPage'),
);

const RecurringOrdersPage = lazy(
  () =>
    import(
      /* webpackChunkName: "RecurringOrdersComponent" */ '../../packages/fundTransfer/src/pages/RecurringOrdersPage'
    ),
);

const FundsTransferPage = lazy(
  () =>
    import(
      /* webpackChunkName: "FundsTransferPageComponent" */ '../../packages/fundTransfer/src/pages/FundsTransferPage'
    ),
);

const TermsConditionPage = lazy(
  () =>
    import(/* webpackChunkName: "TermsConditionPageComponent" */ '../../packages/userProfile/src/pages/TermsCondition'),
);

const ExchangeCalculatorPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ExchangeCalculatorPageComponent" */ '../../packages/fundTransfer/src/pages/ExchangeCalculatePage'
    ),
);

const FavouriteTransfersPage = lazy(
  () =>
    import(
      /* webpackChunkName: "FavouriteTransfersPageComponent" */ '../../packages/fundTransfer/src/pages/FavouriteTransfersPage'
    ),
);

const ActivityLogPage = lazy(
  () =>
    import(/* webpackChunkName: "ActivityLogPageComponent" */ '../../packages/activityLog/src/pages/ActivityLogPage'),
);

const ActivityLogDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ActivityLogDetailsPageComponent" */ '../../packages/activityLog/src/pages/ActivityLogDetailsPage'
    ),
);

const NewIbanAccountPage = lazy(
  () =>
    import(/* webpackChunkName: "NewIbanAccountPageComponent" */ '../../packages/account/src/pages/NewIbanAccountPage'),
);

const useStyles = makeStyles(theme => ({
  appContainer: {
    position: 'relative',
  },
  suspenseLoader: {
    height: '100vh',
    position: 'absolute',
    top: '50%',
    right: '50%',
  },
}));

const App: FC<GenericObject> = ({ brandConfig }) => {
  const language = useLanguage();
  const { consentList, requiredApproved } = useAppAuthenticationState();
  const classes = useStyles();
  const isCardHolder = useIsCardHolder();
  return (
    <Box data-testid='app' data-version={process.env.REACT_APP_VERSION} className={classes.appContainer}>
      <GoogleAnalyticsPageView />
      <AppHelmetProvider locale={language?.cultureName}>
        <Header brandConfig={brandConfig} hasHeaderElements={!!requiredApproved} />
        <Suspense
          fallback={
            <Box className={classes.suspenseLoader}>
              <ECOMMBXLoader size='full' verticalSpacing='large' />
            </Box>
          }>
          <ErrorBoundary FallbackComponent={() => <ErrorPage pageConfig={brandConfig.errorPage} />}>
            <Routes>
              <Route
                path='/dashboard'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <DashboardProvider>
                        {isCardHolder ? (
                          <CardsProvider isCardHolderDashboard>
                            <CardDetailsWrapper>
                              <CardActionsProvider>
                                <DashboardCardHolderPage pageConfig={brandConfig.dashboardPage} />
                              </CardActionsProvider>
                            </CardDetailsWrapper>
                          </CardsProvider>
                        ) : (
                          <DashboardPage pageConfig={brandConfig.dashboardPage} />
                        )}
                      </DashboardProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/transactions/*'
                element={
                  <ConsolidatedTransactionsRouter
                    requiredEntitlements={UserAppTransaction.FundsTransferHistory}
                    brandConfig={brandConfig}
                  />
                }
              />
              <Route path='/individual/*' element={<IndividualCardsRouter brandConfig={brandConfig} />} />
              <Route path='/corporate/*' element={<CorporateCardsRouter brandConfig={brandConfig} />} />
              <Route path='/accounts/*' element={<AccountsRouter brandConfig={brandConfig} />} />
              <Route
                path='/beneficiaries'
                element={
                  <SecureRoute requiredEntitlements={UserAppTransaction.AliasList}>
                    <EligibleRoute>
                      <BeneficiariesProvider>
                        <FavoriteBeneficiariesPage pageConfig={brandConfig.favoriteBeneficiariesPage} />
                      </BeneficiariesProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/beneficiaries/new'
                element={
                  <SecureRoute requiredEntitlements={UserAppTransaction.AliasList}>
                    <EligibleRoute>
                      <CreateFavoriteBeneficiaryPage pageConfig={brandConfig.createFavoriteBeneficiaryPage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/communication-preferences'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <CommunicationProvider>
                        <CommunicationPreferencesPage pageConfig={brandConfig.communicationPreferencesPage} />
                      </CommunicationProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/user-profile'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <UserProfilePage pageConfig={brandConfig.userProfilePage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/policies/gdpr'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <GDPRPage pageConfig={brandConfig.gdprPage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/device-management'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <DeviceManagementProvider>
                        <DeviceManagementPage pageConfig={brandConfig.deviceManagementPage} />
                      </DeviceManagementProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/security-preferences'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <SecurityPreferencesPage pageConfig={brandConfig.securityPreferencesPage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/security-preferences/change-password'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <SecurityPreferencesChangePasswordPage
                        pageConfig={brandConfig.securityPreferencesChangePasswordPage}
                      />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/security-preferences/change-username'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <SecurityPreferencesChangeUsernamePage
                        pageConfig={brandConfig.securityPreferencesChangeUsernamePage}
                      />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/error'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <ErrorPage pageConfig={brandConfig.errorPage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/success'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <SuccessPage pageConfig={brandConfig.successPage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/transfers/mandates'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <AccountListProvider>
                        <AuthorizationsListPage pageConfig={brandConfig.authorizationsListPage} />
                      </AccountListProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/transfers/mandates/:transactionNo/details'
                element={<WrappedAuthorizationsDetailsPage pageConfig={brandConfig.authorizationDetailsPage} />}
              />
              <Route
                path='/recurring-orders'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <RecurringOrdersProvider>
                        <RecurringOrdersPage pageConfig={brandConfig.recurringOrdersPage} />
                      </RecurringOrdersProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/mass-payments'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <MassPaymentsPage pageConfig={brandConfig.massPaymentsPage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/payroll'
                element={
                  <SecureRoute requiredEntitlements={UserAppTransaction.PayrollList}>
                    <EligibleRoute>
                      <PayrollProvider>
                        <PayrollPage pageConfig={brandConfig.payrollPage} />
                      </PayrollProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/payroll/:payrollId/details'
                element={
                  <SecureRoute requiredEntitlements={UserAppTransaction.PayrollList}>
                    <EligibleRoute>
                      <PayrollProvider>
                        <PayrollDetailsPage pageConfig={brandConfig.payrollDetailsPage} />
                      </PayrollProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/payroll/new'
                element={
                  <SecureRoute
                    requiredEntitlements={[UserAppTransaction.PayrollList, UserAppTransaction.ImportPayrollFile]}>
                    <EligibleRoute>
                      <AccountListProvider>
                        <NewPayrollPage pageConfig={brandConfig.newPayrollPage} />
                      </AccountListProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/fund-transfer'
                element={
                  <SecureRoute requiredEntitlements={UserAppTransaction.TransferOrderWithRouting}>
                    <EligibleRoute>
                      <AccountListProvider>
                        <EnhancedAliasProvider>
                          <FundTransferProvider>
                            <FavouriteTransfersProvider>
                              <BeneficiariesProvider hasPagination={false}>
                                <FundsTransferPage pageConfig={brandConfig.fundsTransferPage} />
                              </BeneficiariesProvider>
                            </FavouriteTransfersProvider>
                          </FundTransferProvider>
                        </EnhancedAliasProvider>
                      </AccountListProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/termsCondition'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <TermsConditionPage pageConfig={brandConfig.termsCondition} consentList={consentList!} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/exchange-calculator'
                element={
                  <SecureRoute requiredEntitlements={UserAppTransaction.ExchangeCalculator}>
                    <EligibleRoute>
                      <ExchangeCalculateProvider>
                        <ExchangeCalculatorPage pageConfig={brandConfig.exchangeCalculatorPage} />
                      </ExchangeCalculateProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/favorite-transfers'
                element={
                  <SecureRoute requiredEntitlements={UserAppTransaction.FavoriteTransactions}>
                    <EligibleRoute>
                      <AccountListProvider>
                        <EnhancedAliasProvider>
                          <FundTransferProvider>
                            <FavouriteTransfersProvider>
                              <FavouriteTransfersPage pageConfig={brandConfig.favouriteTransfersPage} />
                            </FavouriteTransfersProvider>
                          </FundTransferProvider>
                        </EnhancedAliasProvider>
                      </AccountListProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/activity-log'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <ActivityLogProvider>
                        <ActivityLogPage pageConfig={brandConfig.activityLogPage} />
                      </ActivityLogProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/activity-log/:activityLogId/details'
                element={
                  <SecureRoute>
                    <EligibleRoute>
                      <ActivityLogProvider>
                        <ActivityLogDetailsPage pageConfig={brandConfig.activityLogDetailsPage} />
                      </ActivityLogProvider>
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='/accounts/new'
                element={
                  <SecureRoute
                    logic={'AND'}
                    requiredEntitlements={[
                      UserAppTransaction.DepositAccounts,
                      UserAppTransaction.RequestAccountOpening,
                    ]}>
                    <EligibleRoute>
                      <NewIbanAccountPage pageConfig={brandConfig.newIbanAccountPage} />
                    </EligibleRoute>
                  </SecureRoute>
                }
              />
              <Route
                path='*'
                element={
                  <SecureRoute>
                    <ErrorPage404 pageConfig={brandConfig.errorPage404} />
                  </SecureRoute>
                }
              />
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </AppHelmetProvider>
    </Box>
  );
};

export { App };

export default App;
